<template>
  <div class="ar-shell">
    <base-dialog :show="!!error" title="An error occurred!" @close="error = null">
      <p>{{ error }}</p>
    </base-dialog>

    <!-- Header -->
    <header class="ar-header">
      <div>
        <div class="eyebrow">Reports</div>
        <h1>My Clients Report</h1>
        <p class="subtitle">Aggregated pay-in / pay-out breakdown across your clients, with the commission charged to each.</p>
      </div>
    </header>

    <!-- Filter panel -->
    <section class="ar-filters">
      <div class="filter-block">
        <div class="filter-block-label">Date Range</div>
        <div class="ar-row">
          <div class="chip-group">
            <button
              v-for="p in datePresets"
              :key="p.value"
              type="button"
              class="chip"
              :class="{ 'chip-active': activePreset === p.value }"
              @click="applyDatePreset(p.value)"
            >{{ p.label }}</button>
          </div>
          <div class="date-inputs">
            <label class="ar-inline-field">
              <span>From</span>
              <input type="date" v-model="startDate" @change="activePreset = 'custom'" class="ar-input ar-input-sm" />
            </label>
            <label class="ar-inline-field">
              <span>To</span>
              <input type="date" v-model="endDate" @change="activePreset = 'custom'" class="ar-input ar-input-sm" />
            </label>
          </div>
        </div>
      </div>

      <div class="filter-actions">
        <div class="filter-actions-buttons">
          <button type="button" class="btn btn-ghost" @click="resetFilters">Reset</button>
          <button type="button" class="btn btn-primary" :disabled="isLoading" @click="generateReport">
            <svg v-if="!isLoading" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span v-else class="btn-spinner"></span>
            {{ isLoading ? 'Generating…' : 'Generate' }}
          </button>
        </div>
      </div>
    </section>

    <!-- Loading -->
    <div v-if="isLoading" class="loading-block">
      <div class="loading-spinner"></div>
      <div class="loading-label">Generating report…</div>
    </div>

    <!-- Empty state -->
    <div v-else-if="!reportLoaded" class="empty-hero">
      <div class="empty-hero-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
          <path d="M3 3v18h18" stroke-linecap="round" />
          <path d="M7 15l4-4 4 4 5-5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </div>
      <div class="empty-hero-title">No report generated yet</div>
      <div class="empty-hero-sub">Pick a date range and click <strong>Generate</strong> to view your clients' report.</div>
    </div>

    <!-- Report content -->
    <template v-else>
      <!-- Summary -->
      <section class="ar-section">
        <div class="section-head">
          <div class="section-title">Summary</div>
          <div class="section-sub">{{ startDate === endDate ? startDate : `${startDate} → ${endDate}` }}</div>
        </div>
        <div class="type-blocks">
          <div v-for="t in types" :key="`sum-${t.value}`" class="type-block">
            <div class="type-badge" :class="'type-' + t.value">
              <span class="type-dot"></span>{{ t.label }}
            </div>
            <div class="status-grid">
              <div v-for="s in t.statuses" :key="`stat-${t.value}-${s}`" class="status-card" :class="'status-' + s">
                <div class="status-name">{{ s }}</div>
                <div class="status-amount">₹{{ fmt(summary[t.value][`${s}Amount`]) }}</div>
                <div class="status-count">
                  {{ (summary[t.value][`${s}Count`] || 0).toLocaleString() }}
                  {{ (summary[t.value][`${s}Count`] || 0) === 1 ? 'order' : 'orders' }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Commission (client only) -->
      <section class="ar-section">
        <div class="section-head">
          <div class="section-title">Commission</div>
          <div class="section-sub">Commission charged on approved transactions only.</div>
        </div>
        <div class="comm-hero">
          <div class="comm-card comm-client">
            <div class="comm-label">Total Commission</div>
            <div class="comm-value">₹{{ fmt(summary.commission.client) }}</div>
            <div class="comm-split">
              Pay-In ₹{{ fmt(summary.commission.payin.client) }}
              · Pay-Out ₹{{ fmt(summary.commission.payout.client) }}
            </div>
          </div>
        </div>

        <div v-if="breakdown.length" class="type-block">
          <div class="section-sub comm-table-cap">By client</div>
          <div class="table-wrap">
            <table class="ar-table">
              <thead>
                <tr>
                  <th class="th-entity">Client</th>
                  <th class="th-comm">Pay-In Commission</th>
                  <th class="th-comm">Pay-Out Commission</th>
                  <th class="th-comm">Total Commission</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in breakdown" :key="`comm-${row.entity}`">
                  <td class="td-entity">{{ row.entity }}</td>
                  <td class="td-amount">₹{{ fmt(row.commission && row.commission.payin.client) }}</td>
                  <td class="td-amount">₹{{ fmt(row.commission && row.commission.payout.client) }}</td>
                  <td class="td-amount td-amount-strong">₹{{ fmt(row.commission && row.commission.client) }}</td>
                </tr>
                <tr class="comm-total-row">
                  <td class="td-entity">TOTAL</td>
                  <td class="td-amount">₹{{ fmt(summary.commission.payin.client) }}</td>
                  <td class="td-amount">₹{{ fmt(summary.commission.payout.client) }}</td>
                  <td class="td-amount td-amount-strong">₹{{ fmt(summary.commission.client) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- Breakdown -->
      <section class="ar-section">
        <div class="section-head">
          <div class="section-title">
            Breakdown by Client
            <span class="head-count">{{ breakdown.length }} {{ breakdown.length === 1 ? 'row' : 'rows' }}</span>
          </div>
          <div class="section-sub">Per-client totals across every status.</div>
        </div>

        <div v-if="breakdown.length === 0" class="empty-mini">No data for the selected filters.</div>

        <div v-else class="type-blocks">
          <div v-for="t in types" :key="`bk-${t.value}`" class="type-block">
            <div class="type-badge" :class="'type-' + t.value">
              <span class="type-dot"></span>{{ t.label }}
            </div>
            <div class="table-wrap">
              <table class="ar-table">
                <thead>
                  <tr>
                    <th class="th-entity" rowspan="2">Client</th>
                    <th v-for="s in t.statuses" :key="`bh-${t.value}-${s}`" colspan="2" class="th-status" :class="'th-status-' + s">{{ s }}</th>
                  </tr>
                  <tr class="tr-sub">
                    <template v-for="s in t.statuses" :key="`bsh-${t.value}-${s}`">
                      <th class="th-sub th-sub-amount">Amount</th>
                      <th class="th-sub th-sub-count">Count</th>
                    </template>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in breakdown" :key="`${t.value}-${row.entity}`">
                    <td class="td-entity">{{ row.entity }}</td>
                    <template v-for="s in t.statuses" :key="`bd-${t.value}-${row.entity}-${s}`">
                      <td class="td-amount">₹{{ fmt(row[t.value][`${s}Amount`]) }}</td>
                      <td class="td-count">{{ (row[t.value][`${s}Count`] || 0).toLocaleString() }}</td>
                    </template>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import http from "../../http-common";
import moment from "moment-timezone";

export default {
  name: "client-partner-report",
  data() {
    const today = moment().tz(process.env.VUE_APP_TIMEZONE || "Asia/Kolkata").format("YYYY-MM-DD");
    return {
      isLoading: false,
      reportLoaded: false,
      error: null,
      startDate: today,
      endDate: today,
      activePreset: "today",
      datePresets: [
        { value: "today", label: "Today" },
        { value: "yesterday", label: "Yesterday" },
        { value: "last7", label: "Last 7 days" },
        { value: "last30", label: "Last 30 days" },
        { value: "thisMonth", label: "This Month" },
        { value: "custom", label: "Custom" },
      ],
      types: [
        { value: "payin", label: "Pay In", statuses: ["approved", "pending", "failed", "expired"] },
        { value: "payout", label: "Pay Out", statuses: ["approved", "pending", "unassigned", "failed"] },
      ],
      summary: this.emptySummary(),
      breakdown: [],
    };
  },
  methods: {
    emptySummary() {
      return {
        payin: { approvedCount: 0, approvedAmount: 0, pendingCount: 0, pendingAmount: 0, failedCount: 0, failedAmount: 0, expiredCount: 0, expiredAmount: 0 },
        payout: { approvedCount: 0, approvedAmount: 0, pendingCount: 0, pendingAmount: 0, unassignedCount: 0, unassignedAmount: 0, failedCount: 0, failedAmount: 0 },
        commission: { payin: { client: 0 }, payout: { client: 0 }, client: 0 },
      };
    },
    fmt(n) {
      const v = Number(n) || 0;
      return v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    applyDatePreset(v) {
      this.activePreset = v;
      const tz = process.env.VUE_APP_TIMEZONE || "Asia/Kolkata";
      const m = () => moment().tz(tz);
      if (v === "today") { this.startDate = this.endDate = m().format("YYYY-MM-DD"); }
      else if (v === "yesterday") { this.startDate = this.endDate = m().subtract(1, "day").format("YYYY-MM-DD"); }
      else if (v === "last7") { this.endDate = m().format("YYYY-MM-DD"); this.startDate = m().subtract(6, "days").format("YYYY-MM-DD"); }
      else if (v === "last30") { this.endDate = m().format("YYYY-MM-DD"); this.startDate = m().subtract(29, "days").format("YYYY-MM-DD"); }
      else if (v === "thisMonth") { this.startDate = m().startOf("month").format("YYYY-MM-DD"); this.endDate = m().format("YYYY-MM-DD"); }
    },
    resetFilters() {
      this.applyDatePreset("today");
    },
    async generateReport() {
      this.isLoading = true;
      this.error = null;
      try {
        const params = { startDate: this.startDate, endDate: this.endDate };
        const r = await http.get("/reports/client-partner-report", { params });
        const d = r.data || {};
        this.summary = d.summary || this.emptySummary();
        this.breakdown = d.breakdown || [];
        this.reportLoaded = true;
      } catch (e) {
        this.error = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || "Failed to generate report.";
      }
      this.isLoading = false;
    },
  },
};
</script>

<style scoped>
.ar-shell { height: 100%; overflow-y: auto; padding: 20px 24px 60px; box-sizing: border-box; }
.ar-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; margin-bottom: 22px; }
.eyebrow { text-transform: uppercase; letter-spacing: .08em; font-size: 11px; font-weight: 700; color: #8a92a6; }
.ar-header h1 { font-size: 24px; font-weight: 700; color: #1b2a47; margin: 4px 0 6px; }
.subtitle { color: #6b7280; font-size: 13px; margin: 0; max-width: 640px; line-height: 1.5; }
.btn { display: inline-flex; align-items: center; gap: 8px; border: 1px solid #d7dbe6; background: #fff; color: #1b2a47; border-radius: 10px; padding: 10px 16px; font-weight: 600; cursor: pointer; font-size: 13px; }
.btn:hover { border-color: #b8bfd0; }
.btn-primary { background: #00296f; border-color: #00296f; color: #fff; }
.btn-primary:disabled { opacity: .6; cursor: default; }
.btn-ghost { background: #f4f6fa; border-color: #eef0f5; }
.btn-icon { width: 16px; height: 16px; }
.btn-spinner { width: 14px; height: 14px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff; border-radius: 50%; animation: spin .8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.ar-filters { background: #fff; border: 1px solid #eef0f5; border-radius: 14px; padding: 18px 20px; display: flex; flex-direction: column; gap: 18px; margin-bottom: 22px; }
.filter-block-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #8a92a6; margin-bottom: 10px; }
.ar-row { display: flex; flex-wrap: wrap; gap: 16px; align-items: center; justify-content: space-between; }
.chip-group { display: flex; flex-wrap: wrap; gap: 8px; }
.chip { border: 1px solid #d7dbe6; background: #fff; color: #465066; border-radius: 999px; padding: 7px 14px; font-size: 12px; font-weight: 600; cursor: pointer; }
.chip-active { background: #00296f; border-color: #00296f; color: #fff; }
.date-inputs { display: flex; gap: 12px; }
.ar-inline-field { display: flex; flex-direction: column; gap: 4px; font-size: 11px; color: #8a92a6; font-weight: 600; }
.filter-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
.ar-field { display: flex; flex-direction: column; gap: 5px; }
.ar-field label { font-size: 12px; font-weight: 600; color: #465066; }
.ar-input { height: 40px; padding: 8px 12px; border: 1px solid #d7dbe6; border-radius: 9px; background: #fff; color: #1b2a47; font-size: 14px; outline: none; box-sizing: border-box; }
.ar-input:focus { border-color: #00296f; box-shadow: 0 0 0 2px rgba(0,41,111,.12); }
.ar-input-sm { height: 36px; font-size: 13px; }
.filter-actions { display: flex; justify-content: flex-end; align-items: flex-end; }
.filter-actions-buttons { display: flex; gap: 10px; }
.loading-block { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 60px; color: #8a92a6; }
.loading-spinner { width: 34px; height: 34px; border: 3px solid #e4e8f0; border-top-color: #00296f; border-radius: 50%; animation: spin .8s linear infinite; }
.empty-hero { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 60px 20px; text-align: center; background: #f8f9fc; border-radius: 14px; }
.empty-hero-icon svg { width: 44px; height: 44px; color: #b8bfd0; }
.empty-hero-title { font-weight: 700; color: #1b2a47; font-size: 16px; }
.empty-hero-sub { color: #8a92a6; font-size: 13px; }
.empty-mini { padding: 24px; text-align: center; color: #8a92a6; background: #f8f9fc; border-radius: 10px; }
.ar-section { background: #fff; border: 1px solid #eef0f5; border-radius: 14px; padding: 20px 22px; margin-bottom: 20px; }
.section-head { margin-bottom: 16px; }
.section-title { font-size: 16px; font-weight: 700; color: #1b2a47; display: flex; align-items: center; gap: 10px; }
.head-count { font-size: 11px; font-weight: 700; color: #8a92a6; background: #f1f3f7; padding: 2px 10px; border-radius: 999px; }
.section-sub { color: #8a92a6; font-size: 12px; margin-top: 4px; }
.type-blocks { display: flex; flex-direction: column; gap: 22px; }
.type-badge { display: inline-flex; align-items: center; gap: 7px; font-weight: 700; font-size: 13px; color: #1b2a47; margin-bottom: 12px; }
.type-dot { width: 9px; height: 9px; border-radius: 50%; }
.type-payin .type-dot { background: #00296f; }
.type-payout .type-dot { background: #7c3aed; }
.status-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 12px; }
.status-card { border: 1px solid #eef0f5; border-radius: 12px; padding: 14px; background: #fcfcfe; }
.status-name { text-transform: capitalize; font-size: 11px; font-weight: 700; color: #8a92a6; letter-spacing: .04em; }
.status-amount { font-size: 18px; font-weight: 700; color: #1b2a47; margin: 6px 0 2px; }
.status-count { font-size: 11px; color: #8a92a6; }
.status-approved { border-color: #cdeedd; background: #f3fcf7; }
.status-pending { border-color: #cfe0f7; background: #f4f9ff; }
.status-failed { border-color: #f6d4d4; background: #fff6f6; }
.status-expired { border-color: #f1d9bc; background: #fff9f1; }
.status-unassigned { border-color: #e2e4ea; background: #fafbfc; }
.comm-hero { margin-bottom: 18px; }
.comm-card { border: 1px solid #cdeedd; background: #f3fcf7; border-radius: 14px; padding: 18px 20px; max-width: 420px; }
.comm-label { font-size: 12px; font-weight: 700; color: #0f7b3f; text-transform: uppercase; letter-spacing: .04em; }
.comm-value { font-size: 28px; font-weight: 800; color: #0b5f31; margin: 6px 0 4px; }
.comm-split { font-size: 12px; color: #4b7a60; }
.comm-table-cap { margin-bottom: 10px; }
.table-wrap { overflow-x: auto; border: 1px solid #eef0f5; border-radius: 10px; }
.ar-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.ar-table th, .ar-table td { padding: 10px 12px; text-align: right; white-space: nowrap; }
.th-entity, .td-entity { text-align: left; font-weight: 600; color: #1b2a47; position: sticky; left: 0; background: #fff; }
.ar-table thead th { background: #f7f8fb; color: #465066; font-weight: 700; font-size: 11px; text-transform: capitalize; border-bottom: 1px solid #eef0f5; }
.th-status { text-align: center; }
.tr-sub .th-sub { font-size: 10px; color: #8a92a6; font-weight: 600; }
.ar-table tbody tr { border-bottom: 1px solid #f4f6fa; }
.ar-table tbody tr:last-child { border-bottom: none; }
.td-amount { color: #1b2a47; font-variant-numeric: tabular-nums; }
.td-amount-strong { font-weight: 700; }
.td-count { color: #8a92a6; font-size: 12px; }
.comm-total-row { background: #f7f8fb; font-weight: 700; }
.comm-total-row .td-entity { background: #f7f8fb; }
.th-status-approved { color: #0f7b3f; }
.th-status-pending { color: #1c5fb0; }
.th-status-failed { color: #cb3a47; }
.th-status-expired { color: #b45309; }
@media (max-width: 900px) { .filter-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 520px) { .filter-grid { grid-template-columns: 1fr; } .ar-row { flex-direction: column; align-items: stretch; } }
</style>
